"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _styles = require("@mui/material/styles");
var _withStyles = _interopRequireDefault(require("@mui/styles/withStyles"));
var _Typography = _interopRequireDefault(require("@mui/material/Typography/Typography.js"));
var _createStyles = _interopRequireDefault(require("@mui/styles/createStyles"));
var _reactSelect = _interopRequireWildcard(require("react-select"));
var _Creatable = _interopRequireDefault(require("react-select/lib/Creatable"));
var _helpers = require("./helpers");
var _MultiValue = _interopRequireDefault(require("./components/MultiValue"));
var _SingleValue = _interopRequireDefault(require("./components/SingleValue"));
var _Option = _interopRequireDefault(require("./components/Option"));
var _MenuList = _interopRequireDefault(require("./components/MenuList"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const muiStyles = theme => (0, _createStyles.default)({
  chip: {
    margin: theme.spacing(0.5, 0.25),
    height: 24,
    maxWidth: '85%'
  },
  chipLabel: {
    padding: '0 10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block'
  },
  icon: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5)
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center'
  }
});
/**
 * Wrapper for react-select with react-virtualized with custom props to make createable, clearable, async and custom styling/themeing
 */
const ThemedSelect = /*#__PURE__*/_react.default.forwardRef((props, ref) => {
  const {
    classes,
    options,
    placeholder,
    label,
    labelPosition = "top",
    mountInputValue,
    createable,
    allowSelectAll,
    blurInputOnSelect = true,
    clearInputOnSelect = true,
    scrollToTopOnClear = true,
    moreOptionsAvailable,
    totalAsyncOptions,
    loadMoreOptions,
    getNewOptions,
    customStyle,
    customTheme,
    customComponents,
    maxFilterHeight,
    ...remainingSelectProps
  } = props;
  const muiTheme = (0, _styles.useTheme)();
  const theme = (0, _helpers.getSelectTheme)(muiTheme, customTheme);
  const styles = (0, _helpers.getSelectStyles)(customStyle, labelPosition, maxFilterHeight);
  const currentIndex = (0, _react.useRef)(0); // track the current list index for rerenders
  const debouncedSearchedText = (0, _react.useRef)(''); // the last searched text when requesting to getNewOptions
  const [isFetchingData, setIsFetchingData] = (0, _react.useState)(false); // true when fetching data to prevent multiple calls
  const [inputValue, setInputValue] = (0, _react.useState)('');

  // on mount
  const componentDidMount = (0, _react.useRef)(false);
  (0, _react.useEffect)(() => {
    if (componentDidMount.current) return;
    componentDidMount.current = true;
    if (mountInputValue) {
      setInputValue(mountInputValue);
      debouncedSearchedText.current = mountInputValue;
    }
  });
  (0, _react.useEffect)(() => {
    setIsFetchingData(false);
  }, [options && options.length, mountInputValue]);
  const handleInputChange = (value, {
    action
  }) => {
    if (clearInputOnSelect && action === 'set-value') {
      setInputValue('');
      debouncedSearchedText.current = '';
    } else if (blurInputOnSelect && action === 'menu-close') {
      setInputValue('');
      debouncedSearchedText.current = '';
    } else if (action === 'input-change') {
      setInputValue(value);
      if (getNewOptions && options && options.length !== totalAsyncOptions) {
        (0, _helpers.debouncedGetNewOptions)(value, debouncedSearchedText, moreOptionsAvailable, getNewOptions, setIsFetchingData);
      }
    }
  };
  const menuListProps = {
    currentIndex,
    loadMoreOptions,
    moreOptionsAvailable,
    isFetchingData,
    setIsFetchingData,
    debouncedSearchedText
  };
  const handleMenuClose = () => {
    if (scrollToTopOnClear) currentIndex.current = 0;
  };
  let placeholderText = placeholder;
  if (!placeholderText) placeholderText = moreOptionsAvailable ? 'Search...' : 'Select...';
  let selectProps = {
    filterOption: (0, _reactSelect.createFilter)({
      ignoreAccents: true
    }),
    theme,
    classes,
    styles,
    components: {
      MenuList: props => /*#__PURE__*/(0, _jsxRuntime.jsx)(_MenuList.default, {
        ...props,
        ...menuListProps
      }),
      MultiValue: _MultiValue.default,
      SingleValue: _SingleValue.default,
      Option: _Option.default,
      ...customComponents
    },
    options,
    placeholder: placeholderText,
    onInputChange: handleInputChange,
    onMenuClose: handleMenuClose,
    inputValue,
    blurInputOnSelect,
    ...remainingSelectProps
  };
  if (allowSelectAll && options) {
    let opts = [...options];
    const allOption = {
      label: 'Select all',
      value: '*'
    };
    const allSelected = props.value && Array.isArray(props.value) && options.length === props.value.length;
    // @ts-ignore
    if (!allSelected && opts.length > 1) opts = [allOption, ...opts];
    selectProps = {
      ...selectProps,
      isMulti: true,
      options: opts,
      onChange: (values, action) => {
        if (!props.onChange) return;
        if (Array.isArray(values) && values.length > 0 && values[values.length - 1].value === allOption.value) {
          return props.onChange(options, action);
        }
        return props.onChange(values, action);
      }
    };
  }
  const selectComponent = createable ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_Creatable.default, {
    ref: ref,
    ...selectProps
  }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactSelect.default, {
    ref: ref,
    ...selectProps
  });
  if (label) {
    switch (labelPosition) {
      case 'left':
        return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          className: classes.flexRow,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
            style: {
              marginRight: 8
            },
            variant: "subtitle1",
            children: label
          }), selectComponent]
        });
      case 'right':
        return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          className: classes.flexRow,
          children: [selectComponent, /*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
            style: {
              marginLeft: 8
            },
            variant: "subtitle1",
            children: label
          })]
        });
      case 'bottom':
        return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          children: [selectComponent, /*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
            variant: "subtitle1",
            children: label
          })]
        });
      default:
        return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
            variant: "subtitle1",
            children: label
          }), selectComponent]
        });
    }
  } else {
    return selectComponent;
  }
});
var _default = exports.default = (0, _withStyles.default)(muiStyles)(ThemedSelect);